@if (!storageReady()) {
  <div class="chat-loading" aria-hidden="true"></div>
} @else if (onboardingOpen()) {
  <app-onboarding (completed)="completeOnboarding($event)" />
} @else {
  <main class="chat-shell" [class.dark]="darkMode()">
    <!-- ── Mobile top header ─────────────────────────────────────────────── -->
    <app-mobile-top-bar
      [displayName]="displayName()"
      [avatarUrl]="avatarUrl()"
      [darkMode]="darkMode()"
      (openProfile)="openProfileModal()"
      (toggleDarkMode)="toggleDarkMode()"
    />

    <!-- ── Sidebar ───────────────────────────────────────────────────────── -->
    <app-chat-sidebar
      [displayName]="displayName()"
      [avatarUrl]="avatarUrl()"
      [darkMode]="darkMode()"
      (openProfile)="openProfileModal()"
      (toggleDarkMode)="toggleDarkMode()"
      (startNewChat)="startNewChat()"
    />

    <!-- ── Conversation panel ─────────────────────────────────────────────── -->
    <section
      class="conversation-panel"
      (click)="closeMessageMenu(); composer?.closePickerFromOutside()"
    >
      @if (isSearching() && !realtime.matched()) {
        <div class="search-state">
          <div class="safety-badge">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Safe &amp; Anonymous
          </div>

          <div class="search-heading">
            <h2>Finding your match</h2>
            <p>
              Scanning for someone who shares your vibe<span
                class="search-dots"
              ></span>
            </p>
          </div>

          <div class="radar-wrap">
            <span class="particle p1" aria-hidden="true">♡</span>
            <span class="particle p2" aria-hidden="true">✦</span>
            <span class="particle p3" aria-hidden="true">♡</span>
            <span class="particle p4" aria-hidden="true">✦</span>
            <span class="particle p5" aria-hidden="true">✧</span>

            <div class="scan-ring sr1"></div>
            <div class="scan-ring sr2"></div>
            <div class="scan-ring sr3"></div>

            <div class="radar-base">
              <div class="radar-sweep"></div>
              <div class="radar-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div class="status-track">
            <div class="status-step step-done">
              <span class="step-dot"></span>
              <span class="step-label">Connected</span>
            </div>
            <div class="status-line"></div>
            <div class="status-step step-active">
              <span class="step-dot"></span>
              <span class="step-label">Searching</span>
            </div>
            <div class="status-line"></div>
            <div class="status-step">
              <span class="step-dot"></span>
              <span class="step-label">Matched!</span>
            </div>
          </div>

          <button class="cancel-button" type="button" (click)="cancelSearch()">
            Cancel
          </button>
        </div>
      } @else if (!realtime.matched()) {
        <div class="idle-state">
          <div class="idle-state-copy">
            <span class="idle-state-icon">♡</span>
            <h2>Choose your interests</h2>
            <p>
              Add a few interests and find someone with something in common.
            </p>
          </div>
          <div class="compact-panel">
            <h3>Match interests</h3>
            <div class="interest-input-wrap">
              <div class="interest-entry">
                <div class="chat-input-wrapper">
                  <textarea
                    autocomplete="off"
                    autocapitalize="sentences"
                    autocorrect="on"
                    spellcheck="false"
                    data-form-type="chat"
                    data-lpignore="true"
                    name="interest"
                    inputmode="text"
                    rows="1"
                    class="chat-input"
                    placeholder="Add interest…"
                    [ngModel]="customInterest()"
                    (ngModelChange)="customInterest.set($event)"
                    (keyup.enter)="addCustomInterest()"
                  ></textarea>
                  <button
                    type="button"
                    class="interest-add-button"
                    (click)="addCustomInterest()"
                    aria-label="Add interest"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="m22 2-7 20-4-9-9-4Z"></path>
                      <path d="M22 2 11 13"></path>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="interest-suggestions">
                @for (interest of interests; track interest) {
                  @if (!selectedInterests().includes(interest)) {
                    <button type="button" (click)="toggleInterest(interest)">
                      + {{ interest }}
                    </button>
                  }
                }
              </div>
              @if (selectedInterests().length > 0) {
                <div class="selected-interest-list">
                  @for (interest of selectedInterests(); track interest) {
                    <span class="selected-interest">
                      {{ interest }}
                      <button
                        type="button"
                        (click)="removeInterest(interest)"
                        [attr.aria-label]="'Remove ' + interest"
                      >
                        ×
                      </button>
                    </span>
                  }
                </div>
              }
            </div>
          </div>
          <button
            class="new-chat-button"
            type="button"
            (click)="startNewChat()"
          >
            Find a match
          </button>
        </div>
      } @else {
        <!-- ── Chat header ──────────────────────────────────────────────── -->
        <app-chat-header
          [name]="realtime.name()"
          [avatarUrl]="realtime.avatarUrl()"
          [country]="realtime.country()"
          [blockConfirmation]="blockConfirmation()"
          [sharedInterests]="realtime.sharedInterests()"
          (block)="block()"
          (cancelBlock)="cancelBlock()"
        />

        <!-- ── Messages ────────────────────────────────────────────────── -->
        <div
          class="messages-area"
          #messagesArea
          (scroll)="onMessagesScroll($event)"
        >
          <div class="chat-start">
            <div class="chat-start-line"></div>
            <div class="chat-start-pill">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z"
                />
              </svg>
              Connected with {{ realtime.name() }}
            </div>
            <div class="chat-start-line"></div>
          </div>
          @for (message of messages(); track message.id) {
            <app-message-bubble
              [message]="message"
              [isMenuOpen]="activeMessageMenu() === message.id"
              (toggleMenu)="toggleMessageMenu($event)"
              (setReply)="setReply($event)"
              (unsend)="unsendMessage($event)"
              (scrollToReply)="scrollToMessage($event)"
              (viewImage)="viewImage($event)"
            />
          }
          @if (realtime.peerIsTyping()) {
            <div class="typing-indicator" aria-label="Peer is typing">
              <span></span><span></span><span></span>
            </div>
          }
        </div>

        <!-- ── New-message floating pill ──────────────────────────────── -->
        @if (showNewMsgPill()) {
          <button class="new-msg-pill" type="button" (click)="scrollToLatest()">
            ↓ {{ unreadWhileScrolled() }} new
            {{ unreadWhileScrolled() === 1 ? "message" : "messages" }}
          </button>
        }

        <!-- ── Composer (includes reply preview strip) ────────────────── -->
        <app-composer
          [draft]="draft()"
          (draftChange)="draft.set($event)"
          [replyTo]="replyTo()"
          [hasDraft]="hasDraft()"
          (send)="send()"
          (skip)="skip()"
          (cancelReply)="cancelReply()"
          (sendImage)="sendImageMessage($event)"
          (typing)="onTyping()"
        />
      }
    </section>

    <!-- ── Profile editing modal ──────────────────────────────────────────── -->
    @if (profileModalOpen()) {
      <app-profile-modal
        [displayName]="displayName()"
        [avatarUrl]="avatarUrl()"
        (save)="saveProfile($event)"
        (close)="closeProfileModal()"
      />
    }
  </main>
}
